import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectIsUserLeader } from '../../redux/organization/organizationSlice';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../tasky/modal';
import {
  getOrganization,
  removeMember,
  updateMemberRole,
  resendInvitation
} from '../../services/api/organizationService';

// 旧 MemberList.scss を内製化した Tailwind 定数。全ルールが .member-list-container 配下に
// スコープされており他ファイルからの上書きはゼロだった。
// テーブルのセル装飾は行ごとに className を撒かず [&_th] / [&_td] で table 側にまとめている。
const ROOT = 'mb-[30px]';
const HEADER = 'flex justify-between items-center mb-5 max-[768px]:flex-col max-[768px]:items-start';
const HEADER_H2 = 'text-[1.5rem] text-primary';
const MEMBER_COUNT = 'text-[1rem] text-[#666]';
const ADD_SECTION = 'max-[768px]:mt-2.5 max-[768px]:w-full';
const ADD_BUTTON =
  'flex items-center bg-primary text-white px-4 py-2 border-none rounded cursor-pointer ' +
  'font-medium transition-colors duration-200 hover:bg-[#3F4FB8] ' +
  'max-[768px]:w-full max-[768px]:justify-center max-[768px]:min-h-[44px]';
const ADD_ICON = 'mr-1.5 text-[1.2rem]';
const LIST_WRAP = 'w-full overflow-x-auto mb-[30px]';
const TABLE =
  'w-full min-w-[680px] border-collapse bg-white rounded-lg shadow-[0_2px_4px_rgba(0,0,0,0.1)] ' +
  'max-[768px]:text-[0.9rem] ' +
  '[&_th]:px-[15px] [&_th]:py-3 [&_th]:text-left [&_th]:border-b [&_th]:border-b-[#eee] ' +
  '[&_th]:bg-[#f5f5f5] [&_th]:font-semibold [&_th]:text-[#333] max-[768px]:[&_th]:p-2 ' +
  '[&_td]:px-[15px] [&_td]:py-3 [&_td]:text-left [&_td]:border-b [&_td]:border-b-[#eee] ' +
  'max-[768px]:[&_td]:p-2 [&_tr:last-child>td]:border-b-0';
// tr:hover は SCSS で .leader-row / .invited-row より後に定義され同特異度で勝っていたため全行に付ける
const ROW_BASE = 'hover:bg-[#f9f9f9]';
const ROW_LEADER =
  `${ROW_BASE} bg-primary/5 [&>td:first-child]:before:content-['👑_'] ` +
  "[&>td:first-child]:before:mr-[5px]";
const ROW_INVITED = `${ROW_BASE} bg-[#fff8e1] opacity-[0.85] [&>td]:text-[#666]`;
const INVITE_BADGE =
  'inline-block ml-2 px-2 py-0.5 text-[0.75rem] font-semibold bg-[#ff9800] text-white rounded-xl align-middle';
const INVITE_STATUS = 'text-[#ff9800] font-medium';
const ACTION_CELL = 'text-center';
const ACTION_BUTTON =
  'bg-transparent text-primary border border-primary px-2.5 py-[5px] rounded ' +
  'cursor-pointer transition-all duration-200 hover:bg-primary hover:text-white ' +
  'disabled:opacity-50 disabled:cursor-not-allowed ' +
  'max-[768px]:px-2 max-[768px]:py-[3px] max-[768px]:text-[0.8rem]';
const NO_MEMBERS =
  'bg-white p-5 text-center rounded-lg shadow-[0_2px_4px_rgba(0,0,0,0.1)] text-[#666]';
const STATE_BOX = 'p-5 text-center';
const STATE_BUTTON =
  'mt-2.5 px-4 py-2 bg-primary text-white border-none rounded cursor-pointer';
const MODAL_OVERLAY =
  'fixed top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center z-[1000]';
const MODAL_BOX =
  'bg-white p-[25px] rounded-lg shadow-[0_4px_15px_rgba(0,0,0,0.2)] max-w-[500px] w-[90%] ' +
  'max-[768px]:w-[95%] max-[768px]:p-[15px]';
const MODAL_TITLE = 'text-[1.5rem] mb-[15px] text-primary';
const MODAL_TEXT = 'mb-5 leading-[1.5]';
const MODAL_ACTIONS = 'flex flex-wrap gap-2.5 max-[768px]:flex-col';
const MODAL_BTN_BASE =
  'flex-1 min-w-[120px] p-2.5 rounded border-none cursor-pointer font-medium ' +
  'transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ' +
  'max-[768px]:w-full';
const BTN_PROMOTE = `${MODAL_BTN_BASE} bg-[#4caf50] text-white enabled:hover:bg-[#3D8B40]`;
const BTN_DEMOTE = `${MODAL_BTN_BASE} bg-[#ff9800] text-white enabled:hover:bg-[#CC7A00]`;
const BTN_REMOVE = `${MODAL_BTN_BASE} bg-[#f44336] text-white enabled:hover:bg-[#EA1C0D]`;
const BTN_CLOSE = `${MODAL_BTN_BASE} bg-[#9e9e9e] text-white enabled:hover:bg-[#858585]`;

interface Member {
  id: number;
  name: string;
  email: string;
  role: 'leader' | 'member';
  ai_usage_score: number;
}

interface Invitation {
  id: number;
  email: string;
  name?: string;
  expires_at: string;
  status: string;
}

interface MemberListProps {
  onMemberUpdate: () => void;
}

export const MemberList: React.FC<MemberListProps> = ({ onMemberUpdate }) => {
  // Reduxのセレクタを使用
  const { showToast } = useToast();
  const confirm = useConfirm();
  const isLeaderFromSelector = useSelector(selectIsUserLeader);
  console.log('MemberList - isLeaderFromSelector:', isLeaderFromSelector);
  
  // 認証ユーザーを直接取得
  const authUser = useSelector((state: any) => state.auth.user);
  console.log('MemberList - authUser:', authUser);
  
  // 直接判定（バックアップメカニズム）
  const [isLeader, setIsLeader] = useState(isLeaderFromSelector);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // 組織情報
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  
  // 招待中メンバー情報
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  
  // メンバー一覧を取得
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        const data = await getOrganization();
        // 組織IDを保存
        setOrganizationId(data.id);
        console.log('Organization data:', data);

        // 取得したユーザーをMember型に変換
        const organizationMembers = data.users?.map(user => {
          console.log('Processing user:', user);
          let userRole: 'leader' | 'member' = 'member';

          if (user.role === 'leader') {
            userRole = 'leader';
          }

          return {
            id: user.id,
            name: user.name || '',
            email: user.email || '',
            role: userRole,
            ai_usage_score: user.ai_usage_score || 0
          };
        }) || [];

        setMembers(organizationMembers);

        // pending_invitations from API
        if (data.pending_invitations) {
          setInvitations(data.pending_invitations);
        }

        // 認証ユーザーがリーダーかどうか再チェック
        if (authUser) {
          const currentUserInList = organizationMembers.find(member => member.id === authUser.id);
          console.log('Current user in members list:', currentUserInList);

          if (currentUserInList?.role === 'leader' || authUser.role === 'leader') {
            console.log('Setting isLeader to true based on fetched data');
            setIsLeader(true);
          }
        }

        setError(null);
      } catch (err) {
        console.error('Failed to fetch members:', err);
        setError('メンバー一覧の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);
  
  
  // 招待メールの再送信
  const handleResendInvitation = async (invitationId: number) => {
    if (!isLeader || formSubmitting) return;

    try {
      setFormSubmitting(true);
      await resendInvitation(invitationId);
      showToast('招待メールを再送信しました', 'success');
    } catch (err) {
      console.error('Failed to resend invitation:', err);
      showToast('招待メールの再送信に失敗しました', 'error');
    } finally {
      setFormSubmitting(false);
    }
  };

  // メンバー詳細モーダル表示
  const handleMemberClick = (member: Member) => {
    if (isLeader) {
      setSelectedMember(member);
      setIsModalOpen(true);
    }
  };

  // メンバー削除処理
  const handleRemoveMember = async (memberId: number) => {
    if (!isLeader || formSubmitting || !organizationId) return;
    
    if (!(await confirm({ title: 'このメンバーを組織から削除しますか？', tone: 'destructive', confirmText: '削除する' }))) {
      return;
    }
    
    try {
      setFormSubmitting(true);
      await removeMember(organizationId, selectedMember?.id || memberId);
      setIsModalOpen(false);
      setSelectedMember(null);
      onMemberUpdate(); // 親コンポーネントに更新を通知
    } catch (err) {
      console.error('Failed to remove member:', err);
      showToast('メンバー削除に失敗しました', 'error');
    } finally {
      setFormSubmitting(false);
    }
  };

  // メンバーロール変更処理
  const handleChangeRole = async (memberId: number, newRole: 'leader' | 'member') => {
    if (!isLeader || formSubmitting || !organizationId) return;
    
    try {
      setFormSubmitting(true);
      await updateMemberRole(organizationId, memberId, newRole);
      setIsModalOpen(false);
      setSelectedMember(null);
      onMemberUpdate(); // 親コンポーネントに更新を通知
    } catch (err) {
      console.error('Failed to update member role:', err);
      showToast('ロール変更に失敗しました', 'error');
    } finally {
      setFormSubmitting(false);
    }
  };

  // 読み込み中表示
  if (loading) {
    return <div className={STATE_BOX}>メンバー情報を読み込み中...</div>;
  }

  // エラー表示
  if (error) {
    return (
      <div className={STATE_BOX}>
        <p>{error}</p>
        <button className={STATE_BUTTON} onClick={() => window.location.reload()}>再読み込み</button>
      </div>
    );
  }

  return (
    <div className={ROOT} data-tutorial="organization-members">
      <div className={HEADER}>
        <h2 className={HEADER_H2}>組織メンバー <span className={MEMBER_COUNT}>({members.length}人{invitations.length > 0 ? ` + 招待中${invitations.length}人` : ''})</span></h2>
        
        {/* メンバー追加リンク（リーダーのみ表示） */}
        {isLeader && (
          <div className={ADD_SECTION}>
            <button 
              className={ADD_BUTTON}
              onClick={() => document.getElementById('member-invite-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className={ADD_ICON}>+</span>
              メンバー追加
            </button>
          </div>
        )}
      </div>

      {(members.length > 0 || invitations.length > 0) ? (
        <div className={LIST_WRAP}>
          <table className={TABLE}>
            <thead>
              <tr>
                <th>名前</th>
                <th>メールアドレス</th>
                <th>ロール</th>
                <th>AI活用スコア</th>
                {isLeader && <th>操作</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id} className={member.role === 'leader' ? ROW_LEADER : ROW_BASE}>
                  <td>{member.name}</td>
                  <td>{member.email}</td>
                  <td>{member.role === 'leader' ? 'リーダー' : 'メンバー'}</td>
                  <td>{member.ai_usage_score || 0}</td>
                  {isLeader && (
                    <td className={ACTION_CELL}>
                      <button
                        className={ACTION_BUTTON}
                        onClick={() => handleMemberClick(member)}
                      >
                        管理
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {invitations.map((invitation) => (
                <tr key={`inv-${invitation.id}`} className={ROW_INVITED}>
                  <td>
                    {invitation.name || '(未設定)'}
                    <span className={INVITE_BADGE}>招待中</span>
                  </td>
                  <td>{invitation.email}</td>
                  <td><span className={INVITE_STATUS}>招待中</span></td>
                  <td>-</td>
                  {isLeader && (
                    <td className={ACTION_CELL}>
                      <button
                        className={ACTION_BUTTON}
                        onClick={() => handleResendInvitation(invitation.id)}
                        disabled={formSubmitting}
                      >
                        再送信
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={NO_MEMBERS}>
          <p>メンバーがいません</p>
        </div>
      )}

      
      {/* メンバー管理モーダル */}
      {isModalOpen && selectedMember && (
        <div className={MODAL_OVERLAY}>
          <div className={MODAL_BOX}>
            <h3 className={MODAL_TITLE}>メンバー管理</h3>
            <p className={MODAL_TEXT}>
              <strong>名前:</strong> {selectedMember.name}<br />
              <strong>メール:</strong> {selectedMember.email}<br />
              <strong>ロール:</strong> {selectedMember.role === 'leader' ? 'リーダー' : 'メンバー'}<br />
            </p>
            
            <div className={MODAL_ACTIONS}>
              {selectedMember.role === 'member' ? (
                <button 
                  className={BTN_PROMOTE}
                  onClick={() => handleChangeRole(selectedMember.id, 'leader')}
                  disabled={formSubmitting}
                >
                  リーダーに昇格
                </button>
              ) : (
                <button 
                  className={BTN_DEMOTE}
                  onClick={() => handleChangeRole(selectedMember.id, 'member')}
                  disabled={formSubmitting}
                >
                  メンバーに降格
                </button>
              )}
              
              <button 
                className={BTN_REMOVE}
                onClick={() => handleRemoveMember(selectedMember.id)}
                disabled={formSubmitting}
              >
                組織から削除
              </button>
              
              <button 
                className={BTN_CLOSE}
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedMember(null);
                }}
                disabled={formSubmitting}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
